<template>
  <section>
    <!-- Display Area with Clear Button -->
    <v-card :color="colorPad" :dark="darkMode" flat class="mb-4">
      <v-card-text class="d-flex justify-content-between py-5 px-3">
        <v-btn icon @click="clear" color="red">
          <v-icon>fal fa-times</v-icon>
        </v-btn>
        <div class="text-center">
          <div class="font-weight-bold text-h4">
            {{ formattedValue }}
          </div>
        </div>
        <div style="width: 48px"></div>
        <!-- Spacer for centering -->
      </v-card-text>
    </v-card>

    <!-- Simplified Numpad -->
    <div class="clock-in-numpad">
      <!-- Row 1: 7, 8, 9 -->
      <v-row class="ma-0">
        <v-col cols="4" class="pa-0">
          <v-btn tile @click="appendToInput('7')" class="numpad-btn">
            <span>7</span>
          </v-btn>
        </v-col>
        <v-col cols="4" class="pa-0">
          <v-btn tile @click="appendToInput('8')" class="numpad-btn">
            <span>8</span>
          </v-btn>
        </v-col>
        <v-col cols="4" class="pa-0">
          <v-btn tile @click="appendToInput('9')" class="numpad-btn">
            <span>9</span>
          </v-btn>
        </v-col>
      </v-row>

      <!-- Row 2: 4, 5, 6 -->
      <v-row class="ma-0">
        <v-col cols="4" class="pa-0">
          <v-btn tile @click="appendToInput('4')" class="numpad-btn">
            <span>4</span>
          </v-btn>
        </v-col>
        <v-col cols="4" class="pa-0">
          <v-btn tile @click="appendToInput('5')" class="numpad-btn">
            <span>5</span>
          </v-btn>
        </v-col>
        <v-col cols="4" class="pa-0">
          <v-btn tile @click="appendToInput('6')" class="numpad-btn">
            <span>6</span>
          </v-btn>
        </v-col>
      </v-row>

      <!-- Row 3: 1, 2, 3 -->
      <v-row class="ma-0">
        <v-col cols="4" class="pa-0">
          <v-btn tile @click="appendToInput('1')" class="numpad-btn">
            <span>1</span>
          </v-btn>
        </v-col>
        <v-col cols="4" class="pa-0">
          <v-btn tile @click="appendToInput('2')" class="numpad-btn">
            <span>2</span>
          </v-btn>
        </v-col>
        <v-col cols="4" class="pa-0">
          <v-btn tile @click="appendToInput('3')" class="numpad-btn">
            <span>3</span>
          </v-btn>
        </v-col>
      </v-row>

      <!-- Row 4: ., 0, <- -->
      <v-row class="ma-0">
        <v-col cols="4" class="pa-0">
          <v-btn tile @click="appendToInput('.')" class="numpad-btn">
            <span>.</span>
          </v-btn>
        </v-col>
        <v-col cols="4" class="pa-0">
          <v-btn tile @click="appendToInput('0')" class="numpad-btn">
            <span>0</span>
          </v-btn>
        </v-col>
        <v-col cols="4" class="pa-0">
          <v-btn tile @click="backspace" class="numpad-btn">
            <v-icon>fal fa-backspace</v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <!-- Row 5: Full-width Submit Button -->
      <v-row class="ma-0">
        <v-col cols="12" class="pa-0">
          <v-btn
            tile
            :disabled="disabled || !canSubmit || loading"
            :loading="loading"
            @click="handleSubmit"
            class="submit-btn"
            block
            color="primary"
          >
            {{ buttonText ? $t(buttonText) : $t("clock_in") }}
          </v-btn>
        </v-col>
      </v-row>
    </div>
  </section>
</template>

<script>
import { myMixin } from "@/mixin/main";

export default {
  name: "ClockInNumpad",
  mixins: [myMixin],
  props: {
    value: {
      type: String,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    buttonText: {
      type: String,
      default: "clock_in",
    },
  },
  data() {
    return {
      windowListener: null,
    };
  },
  mounted() {
    try {
      this.windowListener = this.handleKeydown.bind(this);
      window.addEventListener("keydown", this.windowListener);
    } catch (error) {
      console.error("Error in mounted lifecycle:", error);
    }
  },
  destroyed() {
    try {
      if (this.windowListener) {
        window.removeEventListener("keydown", this.windowListener);
      }
    } catch (error) {
      console.error("Error in destroyed lifecycle:", error);
    }
  },
  computed: {
    colorPad() {
      try {
        return localStorage.getItem("darkMode") === "true" ? "#333" : "#f0f0f0";
      } catch (error) {
        console.error("Error getting color pad:", error);
        return "#f0f0f0";
      }
    },
    darkMode() {
      try {
        return localStorage.getItem("darkMode") === "true";
      } catch (error) {
        return false;
      }
    },
    formattedValue() {
      try {
        const formatted = this.$options.filters.NumberFormat(this.value);
        return formatted;
      } catch (error) {
        console.error("Error formatting value:", error);
        return this.value ? this.value.toString() : "0.00";
      }
    },
    canSubmit() {
      try {
        const currentValue = parseFloat(this.value) || 0;
        return currentValue > 0;
      } catch (error) {
        return false;
      }
    },
  },
  methods: {
    appendToInput(value) {
      try {
        if (!value || typeof value !== "string") return;

        // Handle decimal point - prevent multiple decimals
        if (value === ".") {
          if (this.value.includes(".")) {
            return; // Already has a decimal point
          }
          // If value is "0", replace it with "0."
          if (this.value === "0" || this.value === "") {
            this.$emit("input", "0.");
            return;
          }
        }

        // If current value is "0" and user enters a number, replace it
        if (this.value === "0" && value !== ".") {
          this.$emit("input", value);
          return;
        }

        this.$emit("input", this.value + value);
      } catch (error) {
        console.error("Error appending to input:", error);
      }
    },
    backspace() {
      try {
        if (!this.value || this.value.length === 0 || this.value.length <= 1) {
          this.$emit("input", "0");
          return;
        }
        // Remove last character
        const newValue = this.value.slice(0, -1);
        // If result is empty or just ".", set to "0"
        if (!newValue || newValue === ".") {
          this.$emit("input", "0");
        } else {
          this.$emit("input", newValue);
        }
      } catch (error) {
        console.error("Error in backspace:", error);
        this.$emit("input", "0");
      }
    },
    clear() {
      try {
        this.$emit("input", "0");
      } catch (error) {
        console.error("Error in clear method:", error);
      }
    },
    handleSubmit() {
      if (this.canSubmit && !this.disabled && !this.loading) {
        this.$emit("submit");
      }
    },
    handleKeydown(event) {
      // Skip if user is typing in another input field
      const activeEl = document.activeElement;
      const tag = activeEl?.tagName?.toLowerCase();
      if (
        tag === "input" ||
        tag === "textarea" ||
        activeEl?.isContentEditable
      ) {
        return;
      }

      if (!event || !event.key) return;

      // Handle number keys
      switch (event.key) {
        case "1":
          event.preventDefault();
          this.appendToInput("1");
          break;
        case "2":
          event.preventDefault();
          this.appendToInput("2");
          break;
        case "3":
          event.preventDefault();
          this.appendToInput("3");
          break;
        case "4":
          event.preventDefault();
          this.appendToInput("4");
          break;
        case "5":
          event.preventDefault();
          this.appendToInput("5");
          break;
        case "6":
          event.preventDefault();
          this.appendToInput("6");
          break;
        case "7":
          event.preventDefault();
          this.appendToInput("7");
          break;
        case "8":
          event.preventDefault();
          this.appendToInput("8");
          break;
        case "9":
          event.preventDefault();
          this.appendToInput("9");
          break;
        case "0":
          event.preventDefault();
          this.appendToInput("0");
          break;
        case ".":
          event.preventDefault();
          this.appendToInput(".");
          break;
        case "Enter":
          event.preventDefault();
          this.handleSubmit();
          break;
        case "Backspace":
          event.preventDefault();
          this.backspace();
          break;
      }
    },
  },
};
</script>

<style scoped>
.clock-in-numpad .numpad-btn {
  height: 78px;
  width: 100%;
  font-weight: 400;
  font-size: 1rem;
  box-shadow: none;
  border-right: 0.1px solid #ccc;
  border-bottom: 0.1px solid #ccc;
}

.clock-in-numpad {
  border-top: 0.1px solid #ccc;
  border-left: 0.1px solid #ccc;
}

.clock-in-numpad .submit-btn {
  height: 60px;
  width: 100%;
  font-weight: 500;
  font-size: 1.1rem;
  border: none;
  box-shadow: none;
  background-color: #438ef2;
  color: #fff;
}

.clock-in-numpad .v-btn.theme--dark:hover {
  background-color: #333;
}

.clock-in-numpad .v-btn.theme--light:hover {
  background-color: #e6e4e4;
}

.clock-in-numpad .submit-btn:hover:not(:disabled) {
  background-color: #357abd !important;
}

.clock-in-numpad .submit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
